import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import Modal from '../components/ui/Modal';
import UiSelect from '../components/ui/Select';

const toArr = x => Array.isArray(x) ? x : (x?.items || []);
const fmtWhen = d => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-';

const LEVELS = [
  { value: 'info',     label: 'Info',     bg: 'rgba(59,130,246,.13)',  color: '#3B82F6' },
  { value: 'success',  label: 'Success',  bg: 'rgba(34,197,94,.12)',   color: '#22C55E' },
  { value: 'warning',  label: 'Warning',  bg: 'rgba(245,158,11,.14)',  color: '#F59E0B' },
  { value: 'critical', label: 'Critical', bg: 'rgba(239,68,68,.13)',   color: '#EF4444' },
];
const levelOf = v => LEVELS.find(l => l.value === v) || LEVELS[0];

const AUDIENCES = [
  { value: 'all',     label: 'All users (every organization)' },
  { value: 'owners',  label: 'Organization owners only' },
  { value: 'org',     label: 'A specific organization' },
];

const EMPTY = { title: '', message: '', level: 'info', audience: 'all', organization_id: '' };

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nt, o] = await Promise.all([
        apiGet(ENDPOINTS.ADMIN.NOTIFICATIONS, { limit: 200 }),
        apiGet(ENDPOINTS.ADMIN.ORGANIZATIONS, { limit: 500 }).catch(() => []),
      ]);
      setItems(toArr(nt));
      setOrgs(toArr(o));
    }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openCompose = () => { setForm(EMPTY); setFormError(''); setOpen(true); };
  const [delId, setDelId] = useState(null);
  const removeNt = async (id) => { setDelId(null); try { await apiDelete(ENDPOINTS.ADMIN.NOTIFICATION(id)); load(); } catch (e) { setError(e.message); } };

  const send = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.title.trim() || !form.message.trim()) { setFormError('Title and message are required.'); return; }
    if (form.audience === 'org' && !form.organization_id) { setFormError('Choose the organization id to target.'); return; }
    setSending(true);
    try {
      await apiPost(ENDPOINTS.ADMIN.NOTIFICATIONS, {
        title: form.title.trim(),
        message: form.message.trim(),
        level: form.level,
        audience: form.audience,
        ...(form.audience === 'org' ? { organization_id: parseInt(form.organization_id) } : {}),
      });
      setOpen(false);
      await load();
    } catch (err) { setFormError(err.message || 'Could not send the notification.'); }
    finally { setSending(false); }
  };

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#A1A1AA', marginBottom: 4 }}>Console / <strong style={{ color: '#FAFAFA' }}>Notifications</strong></div>
          <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 44, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.05 }}>
            <em style={{ fontStyle: 'italic', background: 'linear-gradient(135deg,#60A5FA,#3B82F6 55%,#2563EB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Notifications</em>
          </h1>
          <p style={{ color: '#A1A1AA', fontSize: 14, margin: '4px 0 0' }}>Broadcast announcements to organizations and users.</p>
        </div>
        <button className="btn btn-primary" onClick={openCompose}><Plus size={16} /> New notification</button>
      </div>

      {error && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div className="card-title" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 14 }}>Sent</div>
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead><tr><th>Level</th><th>Title</th><th>Message</th><th>Audience</th><th>Sent</th><th style={{ width: 40 }}></th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#A1A1AA' }}>Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#71717A' }}>No notifications sent yet</td></tr>}
            {items.map(nt => {
              const lv = levelOf(nt.level);
              return (
                <tr key={nt.id}>
                  <td><span style={{ fontFamily: 'monospace', fontSize: 10, padding: '2px 8px', borderRadius: 5, fontWeight: 600, background: lv.bg, color: lv.color }}>{lv.label}</span></td>
                  <td style={{ color: '#FAFAFA', fontWeight: 500 }}>{nt.title}</td>
                  <td style={{ color: '#A1A1AA', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nt.message}</td>
                  <td style={{ color: '#D4D4D8', fontSize: 12 }}>{nt.audience === 'org' ? (nt.organization_name || `Org #${nt.organization_id}`) : (AUDIENCES.find(a => a.value === nt.audience)?.label || nt.audience)}</td>
                  <td className="tbl-mono">{fmtWhen(nt.created_at)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button className="mini-btn" title="Delete" onClick={() => setDelId(nt.id)} style={{ color: '#EF4444' }}><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => !sending && setOpen(false)} title="New notification" size="lg">
        <form onSubmit={send} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {formError && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13 }}>{formError}</div>}
          <div className="field">
            <label className="field-label">Title *</label>
            <input className="field-input" value={form.title} onChange={e => setF('title', e.target.value)} placeholder="e.g. Scheduled maintenance" required />
          </div>
          <div className="field">
            <label className="field-label">Message *</label>
            <textarea className="field-input" value={form.message} onChange={e => setF('message', e.target.value)} placeholder="What do you want to tell them?" rows={4} style={{ resize: 'vertical' }} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field">
              <label className="field-label">Level</label>
              <UiSelect value={form.level} onChange={v => setF('level', v)} options={LEVELS.map(l => ({ value: l.value, label: l.label }))} />
            </div>
            <div className="field">
              <label className="field-label">Audience</label>
              <UiSelect value={form.audience} onChange={v => setF('audience', v)} options={AUDIENCES} />
            </div>
          </div>
          {form.audience === 'org' && (
            <div className="field">
              <label className="field-label">Organization *</label>
              <UiSelect
                value={form.organization_id}
                onChange={v => setF('organization_id', v)}
                placeholder="Select an organization…"
                searchable
                options={orgs.map(o => ({ value: o.id, label: o.name }))}
              />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)} disabled={sending}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={sending}>{sending ? 'Sending…' : 'Send notification'}</button>
          </div>
        </form>
      </Modal>

      <Modal open={delId != null} onClose={() => setDelId(null)} title="Delete broadcast" size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ fontSize: 13, color: '#A1A1AA', margin: 0, lineHeight: 1.6 }}>Delete this broadcast? It is removed for all recipients along with their read markers. This can't be undone.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setDelId(null)}>Cancel</button>
            <button className="btn btn-danger btn-sm" onClick={() => removeNt(delId)}>Delete</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
